import { ArrowRight, Check } from "lucide-react"

import { ServiceIcon } from "./EntryPicker"
import { BundleDeliverables } from "./BundleDeliverables"
import { bundleDeliverables } from "@/lib/post/bundle"
import { cn } from "@/lib/utils"

// ServiceSetupHub — the "Set up your services" hub (CONFIG-SCHEMA §3, the design's
// `queue` view). This is conceptually the third stepper node ("Set up"); the page
// wraps it in <WizardShell> for the stepper, the project-name eyebrow, the price
// rail and the Back / Save & exit / Continue footer.
//
// It is a hub-and-spoke launchpad: every CONFIGURABLE service is a row with its
// own Set up button (the first unconfigured one is the dark primary CTA, the rest
// are ghost). Clicking a row drops into that service's wizard; finishing it lands
// back here. A bundle additionally lists its included deliverables as read-only
// "Included" rows and folds the optional bundle-materials uploads in below
// (matching start-project.html's `queue` view).
//
// Props:
//   catalog    — props.catalog (read service name/icon by key)
//   state      — the wizard state (path, bundle, bundleTier, postings, bundleExtras)
//   ctx        — the wizard ctx (rates + outlinePrices); makes bundleDeliverables value
//                cards server-sourced / Insider-aware (C2). Absent → RATES seed fallback.
//   queue      — the configurable service keys, in order (book_writing+cover for a
//                bundle, the selected services for build-your-own)
//   configured — (key) => bool; true once every required step of that service is set
//   onSetup    — (queueIndex) => configure the service at that index
//   setExtra / setFile — bundle-materials writers (forwarded to BundleDeliverables)

// The included (non-configured) bundle deliverables map to these glyphs by key.
const DELIVERABLE_ICON = {
  outline: "List",
  description: "AlignLeft",
  formatting: "LayoutTemplate",
  narration: "Mic",
  cover: "Image",
}

function HubRow({ icon, name, sub, status, isNext, onClick, onCopyBookDetails }) {
  const done = status === "done"
  const included = status === "included"

  return (
    <div
      className={cn(
        "grid grid-cols-[40px_minmax(0,1fr)] items-center gap-3 rounded-[13px] border p-4 transition-colors sm:flex sm:gap-4",
        isNext ? "border-primary ring-[3px] ring-primary/10" : "border-border",
        "bg-card"
      )}
    >
      <span
        className={cn(
          "grid size-10 shrink-0 place-items-center rounded-[11px] border",
          done || included ? "border-transparent bg-[var(--green-soft,var(--secondary))] text-done" : "border-border bg-secondary text-foreground"
        )}
      >
        <ServiceIcon name={icon} className="size-5" />
      </span>

      <div className="min-w-0 flex-1">
        <h4 className="flex items-center gap-2 font-display text-[14px] font-bold tracking-tight text-foreground">
          {name}
          {done && <Check className="size-3.5 text-done" strokeWidth={3} />}
        </h4>
        {sub && <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground text-pretty">{sub}</p>}
      </div>

      {included ? (
        <span className="col-span-2 justify-self-end whitespace-nowrap rounded-md bg-[var(--green-soft,var(--secondary))] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-done sm:shrink-0">
          Included
        </span>
      ) : (
        <div className="col-span-2 flex min-w-0 flex-col items-end gap-1.5 sm:shrink-0">
          {onCopyBookDetails && (
            <button
              type="button"
              onClick={onCopyBookDetails}
              title="Copies empty compatible fields as an editable snapshot. Existing service details stay unchanged."
              className="text-[11.5px] font-semibold text-primary underline-offset-4 hover:underline"
            >
              Copy details from Book Writing
            </button>
          )}
          <button
            type="button"
            onClick={onClick}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[9px] px-[15px] py-2.5 text-[12.5px] font-semibold transition-colors",
              isNext
                ? "bg-foreground text-background hover:bg-foreground/90"
                : "border border-line-2 bg-card text-muted-foreground hover:border-ink-3 hover:text-foreground"
            )}
          >
            {done ? "Edit" : "Set up"}
            <ArrowRight className="size-3.5" strokeWidth={2.2} />
          </button>
        </div>
      )}
    </div>
  )
}

export function ServiceSetupHub({
  catalog,
  state,
  ctx = {},
  queue,
  configured,
  canCopyBookDetails,
  onCopyBookDetails,
  onSetup,
  setExtra,
  setFile,
  uploadStatuses,
  retryUpload,
  retryingUpload,
}) {
  const serviceByKey = (catalog.services || []).reduce((map, svc) => {
    map[svc.key] = svc
    return map
  }, {})

  const firstUnconfigured = queue.findIndex((key) => !configured(key))

  // Per-row sub copy (audit F): the manuscript row names what it sets; every
  // other configurable row uses the generic design sub.
  const rowSub = (key) =>
    key === "book_writing"
      ? "Set the manuscript details, type, length, tier and more."
      : "Set up the details for this service."

  // The configured rows the customer drives directly (book_writing + cover for a
  // bundle, every selected service for build-your-own).
  const rows = queue.map((key, i) => {
    const svc = serviceByKey[key] || {}
    const done = configured(key)
    return {
      key,
      icon: svc.icon,
      name: svc.name || key,
      sub: done ? "Configured. Edit any detail anytime." : rowSub(key),
      status: done ? "done" : "todo",
      isNext: i === firstUnconfigured,
      onCopyBookDetails:
        canCopyBookDetails?.(key) && onCopyBookDetails
          ? () => onCopyBookDetails(key)
          : null,
      onClick: () => onSetup(i),
    }
  })

  // The bundle-tier short name for the included-row sub ("Gold Bundle" → "Gold").
  const bundleShort = state.bundle
    ? ((catalog.bundles || []).find((b) => b.bundleTier === state.bundleTier)?.name || "").replace(/ Bundle$/, "")
    : ""

  // A bundle also lists what it includes (handled by the team) as read-only rows,
  // each sub naming the tier it belongs to (audit F).
  const includedRows = state.bundle
    ? bundleDeliverables(state, ctx)
        .filter((d) => d.key !== "cover")
        .map((d) => ({
          key: `deliv-${d.key}`,
          icon: DELIVERABLE_ICON[d.key] || "FileText",
          name: d.title,
          sub: `Included in the ${bundleShort} bundle, handled by your team.`,
          status: "included",
        }))
    : []

  const showMaterials = !!state.bundle

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        {rows.map((row) => (
          <HubRow key={row.key} {...row} />
        ))}
        {includedRows.map((row) => (
          <HubRow key={row.key} {...row} />
        ))}
      </div>

      {showMaterials && (
        <div className="border-t border-border pt-5">
          <BundleDeliverables
            state={state}
            ctx={ctx}
            setExtra={setExtra}
            setFile={setFile}
            uploadStatuses={uploadStatuses}
            retryUpload={retryUpload}
            retryingUpload={retryingUpload}
            showIncluded={false}
          />
        </div>
      )}
    </div>
  )
}
